/**
 * ConformancePanel — displays violations or a pass state for schema conformance.
 *
 * Validates exported columns against a ConformanceContract. If violations are
 * found, renders a checklist of human-readable issues. If compliant, shows
 * "Conforms to <source> contract ✓". If contract is null, renders nothing.
 *
 * Imported from @capture/shared:
 * - validateConformance(columns, contract) → Violation[]
 * - ViolationKind (type discriminant for violation.kind)
 * - ConformanceContract (the schema + policy)
 * - Violation (kind, field, detail?)
 */

import { validateConformance, type Violation, type ConformanceContract, type ExportColumnInput } from "@capture/shared";
import type { FormatColumn } from "./FormatColumnsEditor";
import styles from "./ConformancePanel.module.css";

export interface ConformancePanelProps {
  columns: FormatColumn[];
  contract: ConformanceContract | null;
}

/**
 * Maps ViolationKind to human-readable copy + optional detail suffix.
 */
function violationText(v: Violation): string {
  switch (v.kind) {
    case "missing_required":
      return `Missing required field: ${v.field}`;
    case "order_mismatch":
      return `Out of order at: ${v.field}${v.detail ? ` (${v.detail})` : ""}`;
    case "type_mismatch":
      return `Type mismatch: ${v.field}${v.detail ? ` (${v.detail})` : ""}`;
    case "constraint_mismatch":
      return `Constraint not met: ${v.field}`;
    case "extra_column":
      return `Not in schema: ${v.field}`;
    // Slot-fulfillment kinds (from validateSlotFulfillment) — not produced by
    // validateConformance, which is all ConformancePanel runs, but ViolationKind
    // is a shared union so the exhaustive switch must handle them.
    case "unfulfilled_required_slot":
      return `Required slot not fulfilled: ${v.field}`;
    case "slot_type_incompatible":
      return `Slot type incompatible: ${v.field}${v.detail ? ` (${v.detail})` : ""}`;
    case "slot_length_incompatible":
      return `Slot length incompatible: ${v.field}${v.detail ? ` (${v.detail})` : ""}`;
    default:
      const _exhaustive: never = v.kind;
      return _exhaustive;
  }
}

/**
 * Readable source label for the contract.
 */
function sourceLabel(source: ConformanceContract["source"]): string {
  const map: Record<ConformanceContract["source"], string> = {
    xsd: "XSD",
    json_schema: "JSON Schema",
    sample_xml: "Sample XML",
    sample_csv: "Sample CSV",
    csv_header: "CSV Header",
  };
  return map[source];
}

export function ConformancePanel({ columns, contract }: ConformancePanelProps) {
  // Render nothing if contract is null — no conformance to validate.
  if (!contract) {
    return null;
  }

  // Cast FormatColumn[] → ExportColumnInput[] (structurally compatible).
  const violations = validateConformance(
    columns as ExportColumnInput[],
    contract
  );

  if (violations.length === 0) {
    // All-conforming state.
    return (
      <div className={styles.conformancePass}>
        <span className={styles.passCheckmark}>✓</span>
        <span className={styles.passText}>
          Conforms to {sourceLabel(contract.source)} contract
        </span>
      </div>
    );
  }

  // Violation list.
  return (
    <div className={styles.conformancePanel}>
      <div className={styles.violationList}>
        {violations.map((v, i) => (
          <div key={i} className={styles.violationItem}>
            <span className={styles.violationBullet}>—</span>
            <span className={styles.violationText}>{violationText(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
